import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { CalendarDays, ChevronLeft, ChevronRight, Clapperboard, Disc3, Film, MonitorPlay, RefreshCw, Tv } from 'lucide-react'
import { api } from './api'
import type { CalendarEvent, CalendarEventKind } from './types'

type CalendarFilter = 'All' | CalendarEventKind

const filterOptions: { value: CalendarFilter; label: string; shortLabel: string; icon: typeof CalendarDays }[] = [
  { value: 'All', label: 'Everything', shortLabel: 'All', icon: CalendarDays },
  { value: 'TvEpisode', label: 'TV episodes', shortLabel: 'TV', icon: Tv },
  { value: 'Theatrical', label: 'Theaters', shortLabel: 'Theaters', icon: Clapperboard },
  { value: 'Digital', label: 'Streaming', shortLabel: 'Streaming', icon: MonitorPlay },
  { value: 'Physical', label: 'Blu-ray', shortLabel: 'Blu-ray', icon: Disc3 }
]

const weekdays = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT']

export function CalendarPage() {
  const [month, setMonth] = useState(() => startOfMonth(new Date()))
  const [filter, setFilter] = useState<CalendarFilter>('All')
  const gridStart = useMemo(() => startOfGrid(month), [month])
  const days = useMemo(() => Array.from({ length: 42 }, (_, index) => addDays(gridStart, index)), [gridStart])
  const rangeEnd = addDays(gridStart, 42)
  const calendar = useQuery({
    queryKey: ['calendar', dateKey(gridStart), dateKey(rangeEnd)],
    queryFn: () => api.calendar(dateKey(gridStart), dateKey(rangeEnd)),
    staleTime: 15 * 60 * 1000
  })
  const monthStartKey = dateKey(month)
  const monthEndKey = dateKey(new Date(month.getFullYear(), month.getMonth() + 1, 1))
  const visibleEvents = useMemo(() => (calendar.data?.events ?? [])
    .filter(event => {
      const eventKey = event.date.slice(0, 10)
      return eventKey >= monthStartKey && eventKey < monthEndKey && (filter === 'All' || event.kind === filter)
    }), [calendar.data?.events, filter, monthEndKey, monthStartKey])
  const eventsByDate = useMemo(() => {
    const grouped = new Map<string, CalendarEvent[]>()
    for (const event of visibleEvents) {
      const key = event.date.slice(0, 10)
      const dayEvents = [...(grouped.get(key) ?? [])]
      const matchingTvIndex = event.kind === 'TvEpisode'
        ? dayEvents.findIndex(item => item.kind === 'TvEpisode' && item.title === event.title)
        : -1
      if (matchingTvIndex >= 0) dayEvents[matchingTvIndex] = { ...dayEvents[matchingTvIndex], subtitle: 'Multiple new episodes' }
      else dayEvents.push(event)
      grouped.set(key, dayEvents)
    }
    return grouped
  }, [visibleEvents])
  const agendaEvents = useMemo(() => {
    const today = dateKey(new Date())
    return visibleEvents.filter(event => event.date.slice(0, 10) >= today).slice(0, 12)
  }, [visibleEvents])
  const upcomingCount = useMemo(() => {
    const today = dateKey(new Date())
    return visibleEvents.filter(event => event.date.slice(0, 10) >= today).length
  }, [visibleEvents])
  const isThisMonth = month.getFullYear() === new Date().getFullYear() && month.getMonth() === new Date().getMonth()

  function moveMonth(offset: number) {
    setMonth(current => new Date(current.getFullYear(), current.getMonth() + offset, 1))
  }

  return (
    <main className="page calendar-page">
      <header className="calendar-hero">
        <div>
          <p className="eyebrow amber">YOUR LIBRARY, ON THE CLOCK</p>
          <h1>Release calendar</h1>
          <p>Premieres, new episodes, streaming arrivals, and discs — all in one place.</p>
        </div>
        <div className="calendar-hero-art" aria-hidden="true"><CalendarDays /><span>{month.toLocaleDateString(undefined, { month: 'short' }).toUpperCase()}</span></div>
      </header>

      <section className="calendar-toolbar" aria-label="Calendar controls">
        <div className="calendar-month-controls">
          <button className="icon-button" onClick={() => moveMonth(-1)} aria-label="Previous month"><ChevronLeft /></button>
          <h2>{month.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}</h2>
          <button className="icon-button" onClick={() => moveMonth(1)} aria-label="Next month"><ChevronRight /></button>
          {!isThisMonth && <button className="calendar-today" onClick={() => setMonth(startOfMonth(new Date()))}>Today</button>}
        </div>
        <div className="calendar-filters" aria-label="Filter releases">
          {filterOptions.map(option => <button key={option.value} className={filter === option.value ? 'active' : ''} onClick={() => setFilter(option.value)} title={option.label}><option.icon /><span className="filter-long">{option.label}</span><span className="filter-short">{option.shortLabel}</span></button>)}
        </div>
      </section>

      {calendar.isError ? <div className="calendar-error"><RefreshCw /><div><strong>The calendar missed its cue.</strong><span>We couldn't load release dates right now. Try this page again in a moment.</span></div></div> :
        <div className="calendar-layout">
          <section className="calendar-month-shell" aria-label={`${month.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })} calendar`}>
            <div className="calendar-weekdays">{weekdays.map(day => <span key={day}>{day}</span>)}</div>
            <div className="calendar-grid">
              {days.map(day => {
                const key = dateKey(day)
                const events = eventsByDate.get(key) ?? []
                const isToday = key === dateKey(new Date())
                const outside = day.getMonth() !== month.getMonth()
                return <div className={`calendar-day${outside ? ' outside' : ''}${isToday ? ' today' : ''}`} key={key}>
                  <div className="calendar-day-number"><span>{day.getDate()}</span>{isToday && <small>TODAY</small>}</div>
                  <div className="calendar-day-events">
                    {calendar.isLoading ? <i className="calendar-event-skeleton" /> : events.slice(0, 3).map(event => <CalendarPill event={event} key={event.id} />)}
                    {events.length > 3 && <span className="calendar-more">+{events.length - 3} more</span>}
                  </div>
                </div>
              })}
            </div>
          </section>

          <aside className="calendar-agenda">
            <header><div><p className="eyebrow">UP NEXT</p><h2>Coming soon</h2></div><span>{upcomingCount} upcoming</span></header>
            {calendar.isLoading ? <div className="calendar-agenda-loading">{Array.from({ length: 5 }, (_, index) => <i key={index} />)}</div> : agendaEvents.length ?
              <div className="calendar-agenda-list">{agendaEvents.map(event => <AgendaItem event={event} key={event.id} />)}</div> :
              <div className="calendar-agenda-empty"><Film /><strong>Quiet on the set</strong><span>No upcoming {agendaFilterLabel(filter)} in this month.</span></div>}
            <footer><span className="calendar-legend-dot tv" /> TV <span className="calendar-legend-dot theatrical" /> Theaters <span className="calendar-legend-dot digital" /> Streaming <span className="calendar-legend-dot physical" /> Blu-ray</footer>
          </aside>
        </div>}
    </main>
  )
}

function CalendarPill({ event }: { event: CalendarEvent }) {
  const content = <><span>{event.title}</span></>
  const className = `calendar-event-pill ${event.kind.toLowerCase()}`
  return event.tmdbId
    ? <Link className={className} to={`/media/${event.mediaType}/${event.tmdbId}`} title={`${event.title} — ${event.subtitle}`}>{content}</Link>
    : <span className={className} title={`${event.title} — ${event.subtitle}`}>{content}</span>
}

function AgendaItem({ event }: { event: CalendarEvent }) {
  const icon = event.kind === 'TvEpisode' ? <Tv /> : event.kind === 'Theatrical' ? <Clapperboard /> : event.kind === 'Digital' ? <MonitorPlay /> : <Disc3 />
  const body = <>
    <div className="calendar-agenda-date"><strong>{formatDay(event.date)}</strong><span>{formatWeekday(event.date)}</span></div>
    <div className={`calendar-agenda-poster ${event.kind.toLowerCase()}`} style={event.posterUrl ? { backgroundImage: `url(${event.posterUrl})` } : undefined}>{!event.posterUrl && icon}</div>
    <div className="calendar-agenda-copy"><strong>{event.title}</strong><span>{event.subtitle}</span><small>{event.kind === 'TvEpisode' ? 'Television' : event.kind === 'Physical' ? 'Physical media' : event.kind}</small></div>
    <ChevronRight className="calendar-agenda-arrow" />
  </>
  return event.tmdbId ? <Link to={`/media/${event.mediaType}/${event.tmdbId}`}>{body}</Link> : <article>{body}</article>
}

function startOfMonth(date: Date) { return new Date(date.getFullYear(), date.getMonth(), 1) }
function startOfGrid(date: Date) { return addDays(startOfMonth(date), -startOfMonth(date).getDay()) }
function addDays(date: Date, amount: number) { const copy = new Date(date); copy.setDate(copy.getDate() + amount); return copy }
function dateKey(date: Date) { return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` }
function localDate(value: string) { const [year, month, day] = value.slice(0, 10).split('-').map(Number); return new Date(year, month - 1, day, 12) }
function formatDay(value: string) { return localDate(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) }
function formatWeekday(value: string) { return localDate(value).toLocaleDateString(undefined, { weekday: 'short' }) }
function agendaFilterLabel(filter: CalendarFilter) {
  return filter === 'TvEpisode' ? 'TV episodes' : filter === 'Theatrical' ? 'theatrical releases' : filter === 'Digital' ? 'streaming releases' : filter === 'Physical' ? 'Blu-ray releases' : 'releases'
}
